import {
  EXTENSION_LIST_EMPTY_ROW_SENTINEL,
  isValidExtensionPolicyEntry,
  isValidPublisherName,
} from '@kirin/api-client/contracts/extension-validators';
import {
  PolicyKey,
  type PolicySetting,
} from '@kirin/api-client/contracts/policies';

/**
 * Validation result type
 */
export type ValidationResult = string | true;

/**
 * Validates a policy setting value based on its type and validation rules.
 * Returns an error message string if validation fails, or true if it passes.
 *
 * @param setting - The policy setting configuration
 * @param value - The value to validate
 * @param policyKey - Extension-list and publisher-list rules apply only when this is {@link PolicyKey.ExtensionsManagement}
 * @returns Error message string or true if valid
 */
export function validatePolicySetting(
  setting: PolicySetting,
  value: unknown,
  policyKey?: string
): ValidationResult {
  // String validation: check minLength, maxLength, and pattern
  if (setting.type === 'string' && typeof value === 'string') {
    if (
      setting.validation?.minLength &&
      value.length < setting.validation.minLength
    ) {
      return `${setting.name}: Minimum length is ${setting.validation.minLength}`;
    }
    if (
      setting.validation?.maxLength &&
      value.length > setting.validation.maxLength
    ) {
      return `${setting.name}: Maximum length is ${setting.validation.maxLength}`;
    }
    // Pattern validation: test against regex pattern from setting metadata
    if (setting.validation?.pattern) {
      try {
        const regex = new RegExp(setting.validation.pattern);
        if (!regex.test(value)) {
          return `${setting.name}: Value must match pattern: ${setting.validation.pattern}`;
        }
      } catch {
        return `${setting.name}: Invalid pattern: ${setting.validation.pattern}`;
      }
    }
  }

  // Array validation: check array length constraints
  // Note: minLength/maxLength are repurposed for array length validation
  if (setting.type === 'array') {
    if (!Array.isArray(value)) {
      return `${setting.name}: Value must be an array`;
    }
    if (
      setting.validation?.minLength &&
      value.length < setting.validation.minLength
    ) {
      return `${setting.name}: Minimum ${setting.validation.minLength} item(s) required`;
    }
    if (
      setting.validation?.maxLength &&
      value.length > setting.validation.maxLength
    ) {
      return `${setting.name}: Maximum ${setting.validation.maxLength} item(s) allowed`;
    }

    if (setting.validation?.arrayItemType === 'string') {
      const list = value as unknown[];
      const applyExtensionsManagementRules =
        policyKey === PolicyKey.ExtensionsManagement;

      if (
        applyExtensionsManagementRules &&
        (setting.key === 'allowedExtensions' ||
          setting.key === 'blockedExtensions')
      ) {
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          if (typeof item !== 'string') {
            return `${setting.name}: Invalid entry at position ${i + 1}`;
          }
          if (
            item === EXTENSION_LIST_EMPTY_ROW_SENTINEL ||
            item.trim() === ''
          ) {
            continue;
          }
          if (!isValidExtensionPolicyEntry(item)) {
            return `${setting.name}: Invalid extension entry (use publisher.extension or publisher.extension@version)`;
          }
        }
      }
      if (
        applyExtensionsManagementRules &&
        (setting.key === 'allowedPublishers' ||
          setting.key === 'blockedPublishers')
      ) {
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          if (typeof item !== 'string') {
            return `${setting.name}: Invalid entry at position ${i + 1}`;
          }
          const t = item.trim();
          if (t === '') {
            continue;
          }
          if (!isValidPublisherName(t)) {
            return `${setting.name}: Invalid publisher name`;
          }
        }
      }
    }
  }

  // Validation passed
  return true;
}
